import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { devices } from "Utils/devices"
import Tooltip from "@mui/material/Tooltip"

const GenericButton = styled.button`
  position: relative;
  height: ${props => props.heightInput};
  width: ${({ width, onlyIcon, heightInput }) => (onlyIcon ? heightInput : width)};
  font-family: Roboto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ onlyIcon, paddingInput }) => (onlyIcon ? "" : paddingInput)};
  background-color: ${props => (props.disableButton ? "grey" : props.backgroundColor)};
  color: ${props => (props.disableButton ? "#fff" : props.color)};
  border: 1px solid ${props => props.borderColor};
  border: ${props => (props.disableButton ? "1px solid grey" : `1px solid ${props.borderColor}`)};
  text-transform: ${({ textTransform }) => (textTransform ? textTransform : "uppercase")};
  font-weight: ${props => (props.fontWeight ? props.fontWeight : "500")};
  font-size: ${({ fontSize }) => (fontSize ? fontSize : "14px")};
  border-radius: 5px;
  transition: 0.2s;
  opacity: ${({ disabled, disableButton }) => (disabled || disableButton ? 0.3 : 1)};
  overflow: hidden;
  cursor: ${({ disabled, disableButton }) => (disabled || disableButton ? "no-drop" : "pointer")};
  span {
    margin-right: ${({ onlyIcon, spaceIcon }) => (onlyIcon ? "" : spaceIcon)};
    font-size: ${({ iconSize }) => (iconSize ? iconSize : "")};
  }
  .buttonText {
    display: ${({ onlyIcon }) => (onlyIcon ? "none" : "block")};
  }
  &:hover {
    background-color: ${props => (props.disableButton ? "grey" : props.backgroundColorHover)};
    color: ${props => (props.disableButton ? "#fff" : props.colorHover)};
    display: flex;
    width: ${({ expandOnHover }) => (expandOnHover ? "fit-content" : "")};
    padding: ${({ expandOnHover }) => (expandOnHover ? "0 20px" : "")};
    .buttonText {
      display: block;
    }
    span {
      margin-right: ${({ expandOnHover }) => (expandOnHover ? "20px" : "")};
    }
  }
  ${({ hideTitleWhenTooSmall }) =>
    hideTitleWhenTooSmall &&
    `
    @media ${devices.laptop} {
      .buttonText {
        display: none;
      };
      span {
        margin-right: 0px;
      }
      width: 50px;
      &:hover{
        .buttonText {
          display: block;
        };
        width: fit-content;
        span {
          margin-right: 5px;
        }
      }
    }
  `}

  ${({ sizeReduceForMobile }) =>
    sizeReduceForMobile &&
    `
    @media ${devices.mobile} {
      width: 100px;
      font-size: 0.7rem;
      height: 45px
    }
    @media ${devices.tablet} {
      width: 150px;
      font-size: 0.8rem;
      height: 45px
    }
  `}

  ${({ isLoading }) =>
    isLoading &&
    `
    background-color: rgba(0, 0, 0, 0.15);
    color: rgba(0, 0, 0, 0);
    border: 1px solid rgba(0, 0, 0, 0.15);
    cursor: default;
    &:hover {
      background-color: rgba(0, 0, 0, 0.15);
      color: rgba(0, 0, 0, 0);
      border: 1px solid rgba(0, 0, 0, 0.15);
    }
  `}
`

const ContainerBeta = styled.div`
  position: absolute;
  top: 1px;
  right: 1px;
  font-size: 0.6rem;
  background: red;
  padding: 2px;
  border-radius: 2px;
`

const Button = ({
  id,
  icon,
  iconSize = "",
  text,
  textTransform = "uppercase",
  fontSize = "14px",
  backgroundColor = "#01014B",
  borderColor,
  color = "#FFF",
  backgroundColorHover,
  borderColorHover,
  colorHover,
  style,
  onClick = () => {},
  fontWeight,
  buttonRole = "button",
  hideTitleWhenTooSmall = false,
  sizeReduceForMobile,
  disableButton = false,
  disabled = false,
  onlyIcon = false,
  expandOnHover = false,
  heightInput = "40px",
  width = "",
  isLoading = false,
  isBeta = false,
  spaceIcon = "20px",
  paddingInput = "0 20px",
  textTooltip = "",
}) => {
  const newId = id || "button" + Date.now()

  const backgroundColorIsLight = backgroundColor => {
    return backgroundColor === "white" || backgroundColor === "transparent"
  }

  const getBackgroundColorHover = ({
    backgroundColorHover,
    backgroundColor,
    borderColor,
    disabled,
  }) => {
    if (disabled) return ""
    else if (backgroundColorHover) return backgroundColorHover
    else if (backgroundColorIsLight(backgroundColor)) return `${borderColor}33`
    else return `${backgroundColor}80`
  }

  const getBorderColorHover = ({ borderColorHover, backgroundColor, borderColor }) => {
    if (borderColorHover) return borderColorHover
    else if (borderColor) return borderColor
    else backgroundColor
  }

  const colorObject = {
    borderColorHover: borderColorHover,
    backgroundColorHover: backgroundColorHover,
    backgroundColor: backgroundColor,
    borderColor: borderColor,
    disabled: disabled,
  }

  return (
    <Tooltip title={textTooltip}>
      <GenericButton
        id={newId}
        role={buttonRole}
        textTransform={textTransform}
        fontSize={fontSize}
        backgroundColor={backgroundColor}
        borderColor={borderColor !== undefined ? borderColor : backgroundColor}
        color={color}
        backgroundColorHover={getBackgroundColorHover(colorObject)}
        borderColorHover={getBorderColorHover(colorObject)}
        colorHover={colorHover ? colorHover : color}
        onlyIcon={onlyIcon || text === undefined}
        fontWeight={fontWeight}
        onClick={disableButton || disabled ? null : onClick}
        style={style}
        hideTitleWhenTooSmall={hideTitleWhenTooSmall}
        sizeReduceForMobile={sizeReduceForMobile}
        disableButton={disableButton}
        disabled={disabled}
        expandOnHover={expandOnHover}
        heightInput={heightInput}
        width={width}
        iconSize={iconSize}
        isLoading={isLoading}
        className={isLoading && "loaderButton"}
        spaceIcon={spaceIcon}
        paddingInput={paddingInput}
      >
        {icon !== undefined && <span>{icon}</span>}
        {text && <div className="buttonText">{text}</div>}
        {isBeta && <ContainerBeta>BETA</ContainerBeta>}
      </GenericButton>
    </Tooltip>
  )
}

Button.propTypes = {
  color: PropTypes.string,
  textTransform: PropTypes.string,
  fontSize: PropTypes.string,
  backgroundColor: PropTypes.string,
  onClick: PropTypes.func,
  buttonRole: PropTypes.string,
  hideTitleWhenTooSmall: PropTypes.bool,
  disableButton: PropTypes.bool,
  disabled: PropTypes.bool,
  onlyIcon: PropTypes.bool,
  expandOnHover: PropTypes.bool,
  heightInput: PropTypes.string,
  width: PropTypes.string,
  iconSize: PropTypes.string,
  isLoading: PropTypes.bool,
  isBeta: PropTypes.bool,
  spaceIcon: PropTypes.string,
  paddingInput: PropTypes.string,
  textTooltip: PropTypes.string,
}

export default Button
